import { Col, message, Row, Space, Typography } from "antd";
import { UploadChangeParam, UploadFile } from "antd/lib/upload";
import React, { useEffect, useState } from "react";
import { getDocumentCount } from "../../common/api/ReviewClient";
import { geturl } from "../../common/api/SafeClient";
// import { listVendors } from "../../common/api/VendorClient";
import { UploadButton } from "../../common/components/UploadButton";
import { handleError } from "../../common/ErrorHandling";
import { ContractCreateButton } from "./components/ContractCreateButton";
import { ContractStatistics } from "./components/ContractStatistics";

interface OrderFormAddTopPanelProps {
    style?: React.CSSProperties;
    onUpload?: (status?: UploadChangeParam<UploadFile>) => void;
    showUpload?: boolean;
    refresh?: number;
}

export const OrderFormAddTopPanel: React.FC<OrderFormAddTopPanelProps> = ({
    style,
    onUpload,
    showUpload = true,
    refresh,
}) => {
    const [contractCount, setContractCount] = useState(0);
    // const [supplierCount, setSupplierCount] = useState(0);

    const ALLOWED_DOCUMENT_EXT = [
        ".pdf",
        ".doc",
        ".docx",
        ".xls",
        ".xlsx",
        ".ppt",
        ".pptx",
        ".txt",
        ".rtf",
        ".md",
        ".csv",
        ".odt",
        ".ods",
        ".odp",
    ];
    const ALLOWED_ARCHIVE_EXT = [".zip"];

    const hasAllowedExtension =
        (allowedExtensions: string[]) =>
        (file: UploadFile): boolean => {
            if (!file?.name) return false;
            const fileExt = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
            return allowedExtensions.includes(fileExt);
        };

    const isDocumentFile = hasAllowedExtension(ALLOWED_DOCUMENT_EXT);
    const isArchiveFile = hasAllowedExtension(ALLOWED_ARCHIVE_EXT);

    const onChangeUpload =
        (isAllowedFile: (file: UploadFile) => boolean) =>
        (status: UploadChangeParam<UploadFile>) => {
            if (!isAllowedFile(status.file)) {
                return;
            }
            const fileList = (status.fileList || []).filter((f) => f && f.name);
            const filteredFileList = fileList.filter(
                (file) => isAllowedFile(file) && file.status !== "error"
            );

            // Create updated status with filtered file list
            const filteredStatus = {
                ...status,
                fileList: filteredFileList,
            };

            const file = status.file;
            if (
                filteredFileList.length > 0 &&
                !file.error &&
                (
                    filteredFileList.map((value) => {
                        return value.status === "done";
                    }) as boolean[]
                ).reduce((prev, current) => {
                    return prev && current;
                }, true)
            ) {
                onUpload?.(filteredStatus);
                message.success("Files successfully uploaded");
                return;
            }
            if (file.error) {
                // Check if there are detailed file validation error messages
                const response = file.response;
                let errorDescription: React.ReactNode;

                if (response?.misc?.invalidFiles && Array.isArray(response.misc.invalidFiles)) {
                    // Display detailed file validation errors
                    const invalidFiles = response.misc.invalidFiles;
                    errorDescription = (
                        <Space direction="vertical" style={{ maxWidth: 600 }}>
                            <span>The following files do not meet upload requirements:</span>
                            {invalidFiles.map(
                                (item: { filename: string; reason: string }, index: number) => (
                                    <div key={index} style={{ paddingLeft: 16 }}>
                                        <Typography.Text strong>{item.filename}</Typography.Text>:{" "}
                                        {item.reason}
                                    </div>
                                )
                            )}
                        </Space>
                    );
                } else {
                    // Default error message
                    errorDescription = (
                        <Space direction="vertical">
                            <span style={{ whiteSpace: "nowrap" }}>
                                File: <Typography.Text italic>{file.name}</Typography.Text>
                            </span>
                            <span>
                                Please{" "}
                                <Typography.Link onClick={() => window.location.reload()}>
                                    refresh the page
                                </Typography.Link>{" "}
                                and try again.
                            </span>
                        </Space>
                    );
                }

                handleError(
                    response,
                    response?.misc?.message || `Upload failed`,
                    errorDescription,
                    { width: "auto" }
                );
            }
        };

    const uploadDocuments = (
        <UploadButton
            accept={ALLOWED_DOCUMENT_EXT.join(",")}
            multiple={true}
            maxCount={10}
            action={geturl("/api/orderform/documents")}
            showUploadList={false}
            beforeUpload={(file) => {
                const isAllowed = isDocumentFile(file);

                if (!isAllowed) {
                    message.error(
                        `Unsupported file type. Please upload one of: ${ALLOWED_DOCUMENT_EXT.join(", ")}`
                    );
                    return false;
                }
                return true;
            }}
            onChange={onChangeUpload(isDocumentFile)}
            title="Upload contract documents"
            description="Click or drag contract files to be analyzed. Supports PDF, Word (.doc/.docx), Excel (.xls/.xlsx), PowerPoint (.ppt/.pptx), text files (.txt/.md/.csv/.rtf), and OpenDocument formats (.odt/.ods/.odp)."
            withCredentials
        />
    );

    const bulkUpload = (
        <UploadButton
            accept={ALLOWED_ARCHIVE_EXT.join(",")}
            multiple={true}
            maxCount={10}
            action={geturl("/api/orderform/zip")}
            showUploadList={false}
            beforeUpload={(file) => {
                const isAllowed = isArchiveFile(file);

                if (!isAllowed) {
                    message.error(
                        `Unsupported file type. Please upload one of: ${ALLOWED_ARCHIVE_EXT.join(", ")}`
                    );
                    return false;
                }
                return true;
            }}
            onChange={onChangeUpload(isArchiveFile)}
            title="Upload ZIP document archives"
            description="Click or drag ZIP files containing contract documents to be analyzed by our automated detection algorithms and validated by our staff."
            withCredentials
        />
    );
    const actions = [
        {
            name: "uploadDocuments",
            action: uploadDocuments,
            isUpload: true,
        },
        {
            name: "bulkUpload",
            action: bulkUpload,
            isUpload: true,
        },
        {
            name: "createButton",
            action: <ContractCreateButton />,
            isUpload: true,
        },
    ];
    const filteredActions = showUpload ? actions : actions.filter((item) => !item.isUpload);

    const loadData = async () => {
        const [contracts /*, vendors */] = await Promise.all([
            getDocumentCount(),
            // listVendors({ pageSize: 10, current: 1 }, [], []),
        ]);

        setContractCount(contracts.total);
        // setSupplierCount(vendors.pagination.total);
    };

    useEffect(() => {
        loadData();
    }, [refresh]);

    return (
        <Row gutter={16} style={style}>
            <Col span={filteredActions.length > 0 ? 5 : 0}>
                <Row gutter={[0, 8]}>
                    {filteredActions.map((item) => (
                        <Col span={24} key={item.name}>
                            {item.action}
                        </Col>
                    ))}
                </Row>
            </Col>
            <Col span={filteredActions.length > 0 ? 19 : 24}>
                <ContractStatistics
                    contractCount={contractCount}
                    // supplierCount={supplierCount}
                />
            </Col>
        </Row>
    );
};
